import {
  createContext,
  createRef,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { t } from '@lingui/macro';

import { type ButtonProps } from '@decisiv/ui-components/lib/components/Button';
import { type ModalProps } from '@decisiv/ui-components/lib/components/Modal/types';

import { usePollDepotBulkImportStatus } from '../hooks/usePollDepotBulkImportStatus';
import { type BulkCreateDepotSavedState } from '../types';

// Lets the Wizard footer's Continue button trigger StepOne's submit (mirrors AddAssetModal).
export const stepOneSubmitRef = createRef<{ submit: () => void }>();

type PollStatus = ReturnType<typeof usePollDepotBulkImportStatus>;

interface BulkCreateDepotHandles {
  stepOneProps: {
    nextButtonProps: ButtonProps;
  };
  wizardProps: {
    activeStep: number;
    intermediate: boolean;
    onCancel: NonNullable<ModalProps['onClose']>;
    submitButtonProps: ButtonProps;
    cancelButtonProps?: ButtonProps;
  };
  savedState: BulkCreateDepotSavedState;
  setSavedState: Dispatch<SetStateAction<BulkCreateDepotSavedState>>;
  setStepOneContinueButtonEnabled: Dispatch<SetStateAction<boolean>>;
  setStepOneContinueButtonLoading: Dispatch<SetStateAction<boolean>>;
  summary: PollStatus['summary'];
  reportUrl: PollStatus['reportUrl'];
  failedToResolve: PollStatus['failedToResolve'];
}

/* istanbul ignore next -- @preserve */
export const BulkCreateDepotContext = createContext<BulkCreateDepotHandles>(
  {} as BulkCreateDepotHandles,
);

interface BulkCreateDepotProviderProps {
  children: ReactNode;
  onClose?: NonNullable<ModalProps['onClose']>;
}

const BulkCreateDepotProvider = ({
  children,
  onClose,
}: BulkCreateDepotProviderProps) => {
  const [savedState, setSavedState] = useState<BulkCreateDepotSavedState>({});

  const [stepOneContinueButtonEnabled, setStepOneContinueButtonEnabled] =
    useState(false);
  const [stepOneContinueButtonLoading, setStepOneContinueButtonLoading] =
    useState(false);

  const { startPolling, summary, reportUrl, pollingFinished, failedToResolve } =
    usePollDepotBulkImportStatus();

  const { importId } = savedState;

  // Start polling once, as soon as the upload returns an import id.
  const startedRef = useRef(false);
  useEffect(() => {
    if (importId && !startedRef.current) {
      startedRef.current = true;
      startPolling(importId);
    }
  }, [importId, startPolling]);

  // Phase: form (no import yet) → processing (polling) → done (resolved).
  const processing = !!importId && !pollingFinished;
  const done = !!importId && pollingFinished;
  const activeStep = done ? 2 : 1;

  const hideModal = useCallback(
    (event: MouseEvent<unknown> | KeyboardEvent) => {
      onClose?.(event);
    },
    [onClose],
  );

  const stepOneProps = useMemo(
    () => ({
      nextButtonProps: {
        onClick: () => stepOneSubmitRef.current?.submit(),
        disabled: !stepOneContinueButtonEnabled,
        loading: stepOneContinueButtonLoading || processing,
        loadingText: processing ? t`Adding Depots...` : t`Loading...`,
      },
    }),
    [stepOneContinueButtonEnabled, stepOneContinueButtonLoading, processing],
  );

  const wizardProps = useMemo(
    () => ({
      activeStep,
      intermediate: processing,
      onCancel: hideModal,
      submitButtonProps: {
        text: t`Close`,
        onClick: hideModal,
      },
      // While processing the wizard disables the primary button, so the visible
      // exit is the cancel slot — relabel it "Close" (the import keeps running).
      cancelButtonProps: processing
        ? { text: t`Close`, variant: 'normal' as const }
        : undefined,
    }),
    [activeStep, processing, hideModal],
  );

  const value = {
    stepOneProps,
    wizardProps,
    savedState,
    setSavedState,
    setStepOneContinueButtonEnabled,
    setStepOneContinueButtonLoading,
    summary,
    reportUrl,
    failedToResolve,
  } satisfies BulkCreateDepotHandles;

  return (
    <BulkCreateDepotContext.Provider value={value}>
      {children}
    </BulkCreateDepotContext.Provider>
  );
};

export const useBulkCreateDepotContext = () =>
  useContext(BulkCreateDepotContext);

export default BulkCreateDepotProvider;
